import React from "react";
import styles from "./time-indicator.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * Time Indicator — a single continuous progress bar that fills over a set
 * duration. Used by auto-rotating carousels to signal how much time remains
 * before the next advance.
 *
 * FUSION ECLIPSE, section "Data display", node 13973:28283. The published
 * frame draws ONE track with ONE fill — not per-item segments — and publishes
 * two heights (`Small` = 4px, `Large` = 6px). The DDS one rule is that the
 * frame beats any other source, so the API here matches the frame rather
 * than the acceptance criteria's "one indicator per card" language.
 *
 * SERVER COMPONENT ON PURPOSE — no hooks, no measurement. The fill is a CSS
 * `@keyframes` that loops every `intervalMs` (infinite iteration), pausing via
 * `animation-play-state` from the `running` prop. Looping — rather than a
 * single-shot animation with `fill-mode: forwards` — is what stops the fill
 * from getting stuck at 100% between the moment the keyframe completes and
 * the moment the carousel actually advances the card. The keyframe returns
 * to 0% at each iteration in sync with the caller's own `intervalMs` tick.
 * The `resetKey` prop is a defensive re-sync: on an out-of-tick card
 * advance (manual click, keyboard nav) the caller changes the value and
 * the fill remounts, restarting the loop from 0% at that moment.
 *
 * DECORATIVE FOR ASSISTIVE TECH. Rendered `aria-hidden` — the enclosing
 * carousel is expected to announce card changes through its own live region,
 * and a second announcer here would compete with it.
 */

/**
 * `small` (4px) and `large` (6px) are the two heights the FUSION ECLIPSE
 * frame publishes. `responsive` is a convenience the carousel uses to match
 * UX's "small on tablet/mobile, large on desktop" ask without the caller
 * owning its own media query — the molecule's CSS flips the height at the
 * 1280px DDS desktop breakpoint.
 */
export type TimeIndicatorSize = "small" | "large" | "responsive";

export interface TimeIndicatorProps {
  /**
   * Duration of the fill, in milliseconds. Clamped to a 100ms floor — WCAG's
   * recommended minimum for auto-advancing content, and defence against a
   * 0/negative that would collapse the animation to instant. `NaN`/`Infinity`
   * fall back to 5000.
   */
  intervalMs: number;
  /**
   * `true` while the caller's timer is running. Toggles the CSS animation
   * via `animation-play-state`, so a pause holds the fill exactly where it
   * was rather than jumping to a state.
   */
  running: boolean;
  /**
   * Change this value to reset the fill to 0 and start again from the
   * beginning. A carousel typically passes its `activeIndex` (or `pageIndex`)
   * here: each card advance is a new value, which remounts the fill.
   */
  resetKey?: string | number;
  /**
   * `small` (4px), `large` (6px), or `responsive` (small on tablet/mobile,
   * large on desktop ≥1280px). Defaults to `small`. The carousel passes
   * `responsive` because UX asked for the 4px bar on tablet/mobile where the
   * row sits in a narrow column.
   */
  size?: TimeIndicatorSize;
  className?: string;
}

const MIN_INTERVAL_MS = 100;
const DEFAULT_INTERVAL_MS = 5000;

const SIZES: Record<TimeIndicatorSize, true> = {
  small: true,
  large: true,
  responsive: true
};

function safeSize(value: string | undefined): TimeIndicatorSize {
  if (value && Object.prototype.hasOwnProperty.call(SIZES, value)) {
    return value as TimeIndicatorSize;
  }
  return "small";
}

function TimeIndicatorImpl({
  intervalMs,
  running,
  resetKey,
  size,
  className
}: TimeIndicatorProps) {
  /* Clamp inside the component so a CMS-driven interval of 0/negative/NaN
   * cannot produce a zero-length animation, which browsers treat as instant
   * and skips the fill entirely — the reader would see the bar go from
   * empty to full with no in-between. */
  const safeInterval = Number.isFinite(intervalMs) ?
    Math.max(MIN_INTERVAL_MS, intervalMs) :
    DEFAULT_INTERVAL_MS;

  const resolvedSize = safeSize(size);
  const classes = [styles.track, styles[resolvedSize], className]
    .filter(Boolean)
    .join(" ");

  return (
    <div aria-hidden="true" className={classes}>
      {/* Keyed on resetKey so React remounts the fill when the caller
       * signals an advance — the CSS keyframe restarts from 0%. When
       * `resetKey` is undefined, a stable "default" key is used so the
       * fill mounts once and keeps its animation state across re-renders
       * driven by other props (e.g. `running` toggling). */}
      <span
        className={styles.fill}
        key={resetKey ?? "default"}
        style={{
          animationDuration: `${safeInterval}ms`,
          animationPlayState: running ? "running" : "paused"
        }}
      />
    </div>
  );
}
export const TimeIndicator = withSafeRender(TimeIndicatorImpl, "TimeIndicator");

export default TimeIndicator;
